import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Badge } from '../../../components/ui/badge'
import { 
  MessageSquare, 
  User, 
  Calendar, 
  Clock, 
  Trash2, 
  Search,
  ChevronDown,
  ChevronRight,
  RefreshCw
} from 'lucide-react'
import { API_BASE_URL } from '../../../lib/config'
import { formatISTDate } from '../../../components/lib/dateUtils'

interface DeletedSession {
  id: string
  session_name: string
  created_at: string
  deleted_at: string
  deleted_by_user: string
  message_count: number
  user: {
    id: string
    username: string
    email: string
    first_name: string
    last_name: string
  }
}

interface DeletedSessionsSectionProps {
  className?: string
}

export function DeletedSessionsSection({ className }: DeletedSessionsSectionProps) {
  const [sessions, setSessions] = useState<DeletedSession[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set())
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    fetchDeletedSessions()
  }, [])

  const fetchDeletedSessions = async () => {
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}admin/deleted-sessions?limit=100`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      })
      
      if (response.ok) {
        const data = await response.json()
        setSessions(data.sessions || [])
      } else {
        console.error('Failed to fetch deleted sessions')
      }
    } catch (error) {
      console.error('Error fetching deleted sessions:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchDeletedSessions()
    setRefreshing(false)
  }

  const toggleSessionExpansion = (sessionId: string) => {
    const newExpanded = new Set(expandedSessions)
    if (newExpanded.has(sessionId)) {
      newExpanded.delete(sessionId)
    } else {
      newExpanded.add(sessionId)
    }
    setExpandedSessions(newExpanded)
  }

  // Filter sessions based on search term
  const filteredSessions = sessions.filter(session => {
    const searchLower = searchTerm.toLowerCase()
    return (
      session.session_name.toLowerCase().includes(searchLower) ||
      session.user.username.toLowerCase().includes(searchLower) ||
      session.user.email.toLowerCase().includes(searchLower) ||
      session.user.first_name?.toLowerCase().includes(searchLower) ||
      session.user.last_name?.toLowerCase().includes(searchLower)
    )
  })

  const getUserDisplayName = (user: DeletedSession['user']) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`
    }
    return user.username || user.email
  }

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-orange-600" />
            Deleted Chat Sessions
          </CardTitle>
          <CardDescription>
            View all chat sessions that have been deleted by users
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
            <span className="ml-2">Loading deleted sessions...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-orange-600" />
              Deleted Chat Sessions
              <Badge variant="secondary" className="ml-2">
                {sessions.length}
              </Badge>
            </CardTitle>
            <CardDescription>
              View all chat sessions that have been deleted by users
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        
        {/* Search */}
        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by session name, username, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </CardHeader>
      
      <CardContent>
        {filteredSessions.length === 0 ? (
          <div className="text-center py-8">
            <Trash2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {searchTerm ? 'No matching deleted sessions' : 'No deleted sessions found'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              {searchTerm 
                ? 'Try adjusting your search terms' 
                : 'Deleted sessions will appear here when users delete their chats'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSessions.map((session) => {
              const isExpanded = expandedSessions.has(session.id)
              const userDisplayName = getUserDisplayName(session.user)
              
              return (
                <div
                  key={session.id}
                  className="border border-orange-200 dark:border-orange-800 rounded-lg p-4 bg-orange-50 dark:bg-orange-900/20"
                >
                  {/* Session Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <MessageSquare className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                        <h4 className="font-medium text-gray-900 dark:text-white truncate">
                          {session.session_name}
                        </h4>
                        <Badge variant="destructive" className="text-xs">
                          Deleted
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center gap-2">
                          <User className="h-3 w-3" />
                          <span className="truncate">
                            {userDisplayName}
                            <span className="ml-1 text-xs">({session.user.email})</span>
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                          <span>Created: {formatISTDate(session.created_at)}</span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Clock className="h-3 w-3" />
                          <span>Deleted: {formatISTDate(session.deleted_at)}</span>
                        </div>
                      </div>
                      
                      <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium">{session.message_count}</span> messages
                      </div>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleSessionExpansion(session.id)}
                      className="flex items-center gap-2 text-orange-600 dark:text-orange-400 border-orange-300 dark:border-orange-700 hover:bg-orange-100 dark:hover:bg-orange-900/30"
                    >
                      {isExpanded ? (
                        <>
                          <ChevronDown className="h-4 w-4" />
                          Hide Details
                        </>
                      ) : (
                        <>
                          <ChevronRight className="h-4 w-4" />
                          View Details
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-orange-200 dark:border-orange-800">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-white mb-2">Session Information</h5>
                          <div className="space-y-1 text-gray-600 dark:text-gray-400">
                            <div><span className="font-medium">Session ID:</span> {session.id}</div>
                            <div><span className="font-medium">Deleted by User ID:</span> {session.deleted_by_user}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-white mb-2">User Information</h5>
                          <div className="space-y-1 text-gray-600 dark:text-gray-400">
                            <div><span className="font-medium">User ID:</span> {session.user.id}</div>
                            <div><span className="font-medium">Username:</span> {session.user.username}</div>
                            <div><span className="font-medium">Email:</span> {session.user.email}</div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          <span className="font-medium">Note:</span> This session was soft-deleted by the user and can be viewed 
                          in the individual user's session history with admin access.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
